import { AxiosError, AxiosResponse } from 'axios'
import debounce from 'lodash/debounce'
import uniqueId from 'lodash/uniqueId'
import type { VFC } from 'react'
import { useEffect, useState } from 'react'
import { serializeJSONApiData, UnauthenticatedHttpClient } from '@opallabs/api'
import { showLegalTakeover } from '@opallabs/brown-dwarf'
import { Size, Spacing } from '@opallabs/gem'
import { type ErrorDocument } from '@opallabs/json-api'
import { ButtonUploader, Conditional } from '@opallabs/kuiper'
import { usePrevious, useServerErrors } from '@opallabs/lambda'
import {
  Button,
  Checkbox,
  InputLabel,
  Link,
  PasswordInputs,
  screenreaderOnly,
  TextInputType,
  TextInputWithLabel,
  TypeAttribute,
} from '@opallabs/lapidary'
import { createAsset } from '@opallabs/wormhole'
import { SsoStatus } from '../types'
import { AvatarPreview } from './avatarPreview'
import { LoginLink } from './LoginLink'
import { SsoLink } from './SsoLink'
import * as styles from './styles'
import { FormProps, FormSuccessResponse, RequestData } from './types'

export const Form: VFC<FormProps> = ({
  companyName,
  currentAvatarUuid,
  currentEmailAddress,
  currentFamilyName,
  currentGivenName,
  currentJobTitle,
  exampleFamilyName,
  exampleGivenName,
  passwordRules,
  ssoIdpName,
  ssoStatus = SsoStatus.Disabled,
  ssoUrl,
  theme = 'light',
  tosAccepted,
  userId,
}) => {
  const [acceptTerms, setAcceptTerms] = useState<boolean>(tosAccepted || false)
  const [emailAddress, setEmailAddress] = useState<string>(
    currentEmailAddress || ''
  )
  const [validEmailAddress, setValidEmailAddress] = useState<boolean>(false)
  const [familyName, setFamilyname] = useState<string>(currentFamilyName || '')
  const [givenName, setGivenname] = useState<string>(currentGivenName || '')
  const [jobTitle, setJobTitle] = useState<string>(currentJobTitle || '')
  const [password, setPassword] = useState<string>('')
  const lastPassword = usePrevious(password)
  const ssoRequired = ssoStatus === SsoStatus.Required
  // Initializing this with `ssoRequired` means `validPassword` will default to
  // `true` if SSO is required.
  const [validPassword, setValidPassword] = useState<boolean>(ssoRequired)
  const [hasRequiredData, setHasRequiredData] = useState<boolean>(true)
  const [avatarUuid, setAvatarUuid] = useState<string | undefined>(
    currentAvatarUuid
  )

  const checkboxHtmlId = uniqueId('ex-astra-account-creation-terms-checkbox')

  const unauthenticatedAPIClient = UnauthenticatedHttpClient()

  const { handleServerError, serverErrors, unsetServerErrors } =
    useServerErrors()

  useEffect(() => {
    if (!acceptTerms || !validEmailAddress || !validPassword) {
      setHasRequiredData(false)
      return
    }

    if (Object.entries(serverErrors).length > 0) {
      setHasRequiredData(false)
      return
    }

    const requiredFields = [emailAddress, familyName, givenName]
    if (!ssoRequired) requiredFields.push(password)

    const invalidField = requiredFields.find((field) => field === '')

    setHasRequiredData(typeof invalidField === 'undefined')
  }, [
    acceptTerms,
    emailAddress,
    familyName,
    givenName,
    password,
    serverErrors,
    ssoRequired,
    validEmailAddress,
    validPassword,
  ])

  useEffect(() => {
    // Without this short-circuit, *every* change to `password` results in
    // `unsetServerErrors` being called. Because of how state is passed to
    // `<PasswordInputs>`, this means that submitting the form and getting back
    // a server error relating to a password field will make `<PasswordInputs>`
    // re-render, which triggers this `useEffect`, and so the server errors
    // automatically be unset immediately after they're set.
    //
    // This short-circuit ensures the expected behavior: if you edit a password
    // input the error will be automatically removed, and if you submit an
    // invalid password the relevant error will be displayed.
    if (lastPassword === password) return

    unsetServerErrors(['confirm_password', 'new_password'])
  }, [lastPassword, password, unsetServerErrors])

  /**
   * @note This callback handles client-side validation.
   */
  function validateEmailAddress(valid: boolean): void {
    setValidEmailAddress(valid)
  }

  /**
   * @note This callback handles server-side validation.
   */
  async function updateEmailAddress(emailAddress: string) {
    try {
      await unauthenticatedAPIClient.get('/users/check_email_availability', {
        headers: {
          'Content-Type': 'application/vnd.api+json',
        },
        params: {
          email_address: emailAddress,
        },
      })
      setEmailAddress(emailAddress)
      unsetServerErrors(['email_address'])
    } catch (error) {
      handleServerError(error as AxiosError<ErrorDocument>)
    }
  }
  const debouncedUpdateEmailAddress = debounce(updateEmailAddress, 500)

  function handleUpload(file: File | string): void {
    if (typeof file === 'string') return
    if (file.type && !file.type.startsWith('image/')) return

    createAsset({
      file,
      onUploadFailure(error) {
        console.error('failure', error)
      },
      onUploadSuccess({ data }) {
        setAvatarUuid(data.id)
      },
    })
  }

  async function submitData() {
    const data = serializeJSONApiData<RequestData>('users', {
      avatar_uuid: avatarUuid || '',
      confirm_password: password,
      email: emailAddress,
      first_name: givenName,
      is_tos_viewed: acceptTerms.toString(),
      is_pp_viewed: acceptTerms.toString(),
      job_title: jobTitle,
      last_name: familyName,
      new_password: password,
    })

    try {
      type Response = AxiosResponse<FormSuccessResponse>
      const response = await unauthenticatedAPIClient.post<
        typeof data,
        Response
      >('/get_started', data, {
        headers: {
          'Content-Type': 'application/vnd.api+json',
        },
      })
      window.location.assign(response.data.destination)
    } catch (error) {
      handleServerError(error as AxiosError<ErrorDocument>)
    }
  }

  return (
    <div css={styles.container}>
      <LoginLink theme={theme} userId={userId} />
      <main css={styles.content}>
        <h2 css={styles.formTitle}>Set up your account</h2>

        <SsoLink
          companyName={companyName}
          ssoIdpName={ssoIdpName}
          ssoStatus={ssoStatus}
          ssoUrl={ssoUrl}
          theme={theme}
        />

        <fieldset css={styles.name}>
          <legend css={screenreaderOnly}>Your Name</legend>
          <TextInputWithLabel
            autocompleteHint="given-name"
            explicitError={serverErrors.given_name}
            initialValue={givenName}
            labelText="First Name"
            onChange={setGivenname}
            placeholder={exampleGivenName}
            required={true}
            theme={theme}
          />
          <TextInputWithLabel
            autocompleteHint="family-name"
            explicitError={serverErrors.family_name}
            initialValue={familyName}
            labelText="Last Name"
            onChange={setFamilyname}
            placeholder={exampleFamilyName}
            required={true}
            styleOverrides={{
              container: {
                marginInlineStart: Spacing.M * 1.125,
              },
            }}
            theme={theme}
          />
        </fieldset>
        <fieldset css={styles.email}>
          <TextInputWithLabel
            autocompleteHint="email"
            explicitError={serverErrors.email_address}
            initialValue={emailAddress}
            labelText="Work Email Address"
            onChange={debouncedUpdateEmailAddress}
            onValidate={validateEmailAddress}
            previouslyModified={true}
            required={true}
            theme={theme}
            type={TextInputType.Email}
          />
        </fieldset>
        {!ssoRequired && (
          <PasswordInputs
            explicitErrors={serverErrors}
            onChange={setPassword}
            onValidate={setValidPassword}
            rules={passwordRules}
            styleOverrides={{
              container: {
                ...styles.fieldset,
                ...styles.siblingFieldset,
              },
              passwordConfirmation: {
                marginInlineStart: Spacing.M * 1.125,
              },
            }}
            theme={theme}
          />
        )}
        <fieldset css={styles.jobTitle}>
          <TextInputWithLabel
            autocompleteHint="organization-title"
            explicitError={serverErrors.job_title}
            initialValue={jobTitle}
            labelText="Job Title"
            onChange={setJobTitle}
            placeholder="e.g. Project Manager"
            theme={theme}
          />
        </fieldset>
        {/* Currently no way to upload assets for unauthenticated users.
            @see https://workwithopal.atlassian.net/browse/TZ-399 */}
        <Conditional condition={!!userId}>
          <fieldset css={styles.avatarUploader}>
            <legend css={screenreaderOnly}>Profile Image</legend>

            <AvatarPreview
              familyName={familyName}
              givenName={givenName}
              size={Size.Icon * 7.5}
              theme={theme}
              uuid={avatarUuid}
            />

            <ButtonUploader
              allowableFiles={['image']}
              theme={theme}
              uploadInstructions={handleUpload}
            />
          </fieldset>
        </Conditional>
        <fieldset css={styles.terms}>
          <Checkbox
            checked={acceptTerms}
            htmlId={checkboxHtmlId}
            onChange={(): void => setAcceptTerms(!acceptTerms)}
            css={styles.checkbox}
            theme={theme}
          />

          <InputLabel
            inputId={checkboxHtmlId}
            text="I agree to the&nbsp;"
            theme={theme}
            valid={true}
          >
            <Link
              href="/popups/terms_and_conditions"
              onClick={(event) =>
                showLegalTakeover(
                  unauthenticatedAPIClient,
                  event,
                  'is_tos_viewed'
                )
              }
              target="_blank"
              theme={theme}
            >
              Terms of Use
            </Link>
            &nbsp;and the&nbsp;
            <Link
              href="/popups/privacy_policy"
              onClick={(event) =>
                showLegalTakeover(
                  unauthenticatedAPIClient,
                  event,
                  'is_pp_viewed'
                )
              }
              target="_blank"
              theme={theme}
            >
              Privacy Policy
            </Link>
          </InputLabel>
        </fieldset>
        <footer css={styles.footer}>
          <Button
            ariaLive="polite"
            buttonText="Create Account"
            variant="primary"
            disabled={!hasRequiredData}
            name="submit"
            onClick={submitData}
            theme={theme}
            typeAttribute={TypeAttribute.submit}
          />
        </footer>
      </main>
    </div>
  )
}
Form.displayName = 'Ex Astra: Form ← AccountCreation'
